import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/store/hooks';
import {
  fetchAttachments,
  deleteAttachment,
  getDownloadUrl,
  clearAttachments,
} from '../../redux/features/attachmentsSlice';
import { AttachmentEntityType } from '../../services/httpServices/attachmentService';
import { ConfirmDialog } from './confirm-dialog';
import { FileText, Image, Download, Trash2, Loader2, AlertCircle } from 'lucide-react';

interface AttachmentListProps {
  entityType: AttachmentEntityType;
  entityId: string;
  allowDelete?: boolean;
}

export function AttachmentList({ entityType, entityId, allowDelete = true }: AttachmentListProps) {
  const dispatch = useAppDispatch();
  const { items, loading, deletingId, downloadingId, error } = useAppSelector(
    (state) => state.attachments,
  );
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; fileName: string } | null>(null);

  useEffect(() => {
    if (entityId) {
      dispatch(fetchAttachments({ entityType, entityId }));
    }
    return () => {
      dispatch(clearAttachments());
    };
  }, [dispatch, entityType, entityId]);

  const handleDownload = async (id: string) => {
    try {
      const { downloadUrl } = await dispatch(getDownloadUrl({ id })).unwrap();
      if (downloadUrl) window.open(downloadUrl, '_blank');
    } catch {
      // The slice keeps `error` in state; the button simply stops spinning.
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    dispatch(deleteAttachment({ id: deleteTarget.id, entityType, entityId }));
    setDeleteTarget(null);
  };

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center gap-2 py-4 text-text-muted">
        <Loader2 size={16} className="animate-spin" />
        <span className="text-sm">Loading attachments...</span>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="flex items-center gap-2 py-4 text-danger">
        <AlertCircle size={16} />
        <span className="text-sm">{error}</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-text-muted">No attachments yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((attachment: any) => {
        const isImage = attachment.fileType?.startsWith('image/');
        return (
          <div
            key={attachment.id}
            className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border hover:border-primary/30 transition-colors"
          >
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              {isImage ? (
                <Image size={18} className="text-primary" />
              ) : (
                <FileText size={18} className="text-primary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-text truncate">{attachment.fileName}</p>
              <p className="text-xs text-text-muted">
                {(attachment.fileSize / 1024 / 1024).toFixed(2)} MB
                {attachment.description && ` · ${attachment.description}`}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleDownload(attachment.id)}
                disabled={downloadingId === attachment.id}
                className="p-2 hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                title="Download"
              >
                {downloadingId === attachment.id ? (
                  <Loader2 size={16} className="animate-spin text-primary" />
                ) : (
                  <Download size={16} className="text-primary" />
                )}
              </button>
              {allowDelete && (
                <button
                  onClick={() => setDeleteTarget({ id: attachment.id, fileName: attachment.fileName })}
                  disabled={deletingId === attachment.id}
                  className="p-2 hover:bg-danger/10 rounded-lg transition-colors disabled:opacity-50"
                  title="Delete"
                >
                  {deletingId === attachment.id ? (
                    <Loader2 size={16} className="animate-spin text-danger" />
                  ) : (
                    <Trash2 size={16} className="text-danger" />
                  )}
                </button>
              )}
            </div>
          </div>
        );
      })}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete attachment?"
        description={
          <>
            This will permanently delete{' '}
            <strong className="text-text">{deleteTarget?.fileName}</strong>. This cannot be undone.
          </>
        }
        confirmLabel="Delete attachment"
        loading={!!deleteTarget && deletingId === deleteTarget.id}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
